import React, { useState, useEffect } from 'react';
import './AssetClassTypeViewer.css';
import ModelStepViewer from '../annotation-layer/ModelStepViewer';
import { TextToSpeech } from '@capacitor-community/text-to-speech';

const AssetClassTypeViewer = ({ url, classIIurl }) => {
    const [title, setTitle] = useState('');
    const [revision, setRevision] = useState('');
    const [date, setDate] = useState('');
    const [chapters, setChapters] = useState([]);
    const [toc, setToc] = useState([]);
    const [modelStepUrls, setModelStepUrls] = useState([]);
    const [currentChapterIndex, setCurrentChapterIndex] = useState(0);
    const [currentSectionIndex, setCurrentSectionIndex] = useState(0);

    // Effect hook to fetch and parse XML data
    useEffect(() => {
        // Function to fetch and parse XML data
        const fetchData = async () => {
            try {
                const targetUrl = classIIurl ? classIIurl : url;
                const response = await fetch(targetUrl);
                const data = await response.text();
                const parser = new DOMParser();
                const xmlDoc = parser.parseFromString(data, "text/xml");
                if (!xmlDoc || !xmlDoc.getElementsByTagName("header")[0]) {
                    console.error("Unexpected XML structure.");
                    return;
                }

                // Parsing logic specific to Class1_Example.xml
                let headerNode = xmlDoc.getElementsByTagName("header")[0];
                if (headerNode) {
                    let titleNode = headerNode.getElementsByTagName("title")[0];
                    if (titleNode && titleNode.childNodes[0]) {
                        setTitle(titleNode.childNodes[0].nodeValue);
                    }

                    let revisionNode = headerNode.getElementsByTagName("revision")[0];
                    if (revisionNode && revisionNode.childNodes[0]) {
                        setRevision(revisionNode.childNodes[0].nodeValue);
                    }

                    let dateNode = headerNode.getElementsByTagName("date")[0];
                    if (dateNode && dateNode.childNodes[0]) {
                        setDate(dateNode.childNodes[0].nodeValue);
                    }
                }

                // Parsing the TOC if exists
                let xmlToc = xmlDoc.getElementsByTagName("toc")[0];
                if (xmlToc) {
                    let parsedToc = [];
                    let xmlTocEntries = xmlToc.getElementsByTagName("entry");
                    for (let i = 0; i < xmlTocEntries.length; i++) {
                        let tocEntry = xmlTocEntries[i];
                        if (tocEntry.childNodes[0]) {
                            let tocTitle = tocEntry.childNodes[0].nodeValue;
                            let tocLink = tocEntry.getAttribute("href");
                            parsedToc.push({ title: tocTitle, link: tocLink });
                        }
                    }
                    setToc(parsedToc);
                }

                // Parsing the chapters
                let parsedChapters = [];
                let xmlChapters = xmlDoc.getElementsByTagName("chapters")[0].getElementsByTagName("chapter");
                for (let i = 0; i < xmlChapters.length; i++) {
                    let chapter = xmlChapters[i];
                    let chapterTitleNode = chapter.getElementsByTagName("title")[0];
                    let chapterTitle = chapterTitleNode && chapterTitleNode.childNodes[0] ? chapterTitleNode.childNodes[0].nodeValue : '';
                    let chapterId = chapter.getAttribute("id");

                    let sections = [];
                    let xmlSections = chapter.getElementsByTagName("section");
                    for (let j = 0; j < xmlSections.length; j++) {
                        let section = xmlSections[j];
                        let sectionTitleNode = section.getElementsByTagName("title")[0];
                        let sectionTitle = sectionTitleNode && sectionTitleNode.childNodes[0] ? sectionTitleNode.childNodes[0].nodeValue : '';
                        let sectionId = section.getAttribute("id");
                        let sectionContentNode = section.getElementsByTagName("content")[0];
                        let sectionContent = sectionContentNode && sectionContentNode.childNodes[0] ? sectionContentNode.childNodes[0].nodeValue : '';

                        sections.push({
                            id: sectionId,
                            title: sectionTitle,
                            content: sectionContent,
                        });
                    }

                    parsedChapters.push({
                        id: chapterId,
                        title: chapterTitle,
                        sections: sections,
                    });
                }
                setChapters(parsedChapters);

                // Extracting model URLs
                let modelStepUrlNodes = xmlDoc.getElementsByTagName("model_step_url");
                let modelStepUrls = [];
                for (let i = 0; i < modelStepUrlNodes.length; i++) {
                    modelStepUrls.push(modelStepUrlNodes[i].textContent);
                }
                setModelStepUrls(modelStepUrls);  // Storing all URLs in the state

            } catch (error) {
                console.error("Error fetching or parsing XML data:", error);
            }
        };

        // Call fetchData function when URL or classIIurl changes
        fetchData();
    }, [url, classIIurl]);

    // Effect hook to auto-play content when chapter or section changes
    useEffect(() => {
        const sectionTitle = chapters[currentChapterIndex]?.sections[currentSectionIndex]?.title || '';
        const sectionContent = chapters[currentChapterIndex]?.sections[currentSectionIndex]?.content || '';

        TextToSpeech.speak({
            text: `${sectionTitle}. ${sectionContent}`,  // This is the text to be read aloud
            locale: 'en_US',
            rate: 1.0,
        });
    }, [currentChapterIndex, currentSectionIndex, chapters]);

    const navigate = (direction) => {
        setCurrentSectionIndex(currentSectionIndex + direction);

        if (currentSectionIndex < 0) {
            setCurrentChapterIndex(currentChapterIndex - 1);
            setCurrentSectionIndex(chapters[currentChapterIndex - 1]?.sections.length - 1);
        } else if (currentSectionIndex >= chapters[currentChapterIndex]?.sections.length) {
            setCurrentChapterIndex(currentChapterIndex + 1);
            setCurrentSectionIndex(0);
        }
    };

    const navigateToTocEntry = (index) => {
        setCurrentChapterIndex(index);
        setCurrentSectionIndex(0);
    };

    return (
        <div>
            <div className="manual-header" style={{ display: currentChapterIndex === 0 && currentSectionIndex === 0 ? 'block' : 'none' }}>
                <h3>{title}</h3>
                {revision && <p>Revision: {revision} | Date: {date}</p>}
            </div>
            {(currentChapterIndex !== 0 || currentSectionIndex !== 0) &&
                <div id="breadcrumb-nav">
                    Chapter {chapters[currentChapterIndex]?.id}: {chapters[currentChapterIndex]?.title} &gt;
                    Section {chapters[currentChapterIndex]?.sections[currentSectionIndex]?.id}: {chapters[currentChapterIndex]?.sections[currentSectionIndex]?.title}
                </div>
            }

            {/* Displaying TOC */}
            <div className="toc">
                {/* <h3>Table of Contents</h3> */}
                <div className="toc-links">
                    {toc.map((entry, index) => (
                        <span key={index} style={{ marginRight: "10px" }}>
                            <button className="toc-button" onClick={() => navigateToTocEntry(index)}>{entry.title}</button>
                        </span>
                    ))}
                </div>

            </div>

            {/* Displaying current chapter and section */}
            <div className="chapter">

                <div className="text-content">
                    <h3>{chapters[currentChapterIndex]?.title}</h3>
                    <h3>{chapters[currentChapterIndex]?.sections[currentSectionIndex]?.title}</h3>
                    <p>{chapters[currentChapterIndex]?.sections[currentSectionIndex]?.content}</p>
                    {/* TTS for the XML Sections */}
                    <button onClick={() => navigate(-1)}>BACK</button>
                    <button onClick={() => navigate(1)}>NEXT</button>
                </div>

                <div className="model-viewer-container">
                    <ModelStepViewer modelUrl={modelStepUrls[currentChapterIndex]} />
                </div>
            </div>
        </div>
    );
};

export default AssetClassTypeViewer;
